import { useCallback, useMemo } from 'react'
import { AppShell, Header, SetupView, type LiteLlmDraft, type SettingsViewProps } from '@saip/ui'
import {
  BackendError,
  createBackendClient,
  createLiteLlmAI,
  createLiteLlmSpeech,
  createMockBackendClient,
  listLiteLlmModels,
  type BackendClient,
} from '@saip/backend'
import {
  SaipProvider,
  defaultLiteLlmBaseUrl,
  useLiteLlmSettings,
  usePromptServer,
} from '@saip/core'
import type { HostAdapter, HostKind } from '@saip/office'
import { App } from '@saip/app'

export interface RootProps {
  host: HostKind
  adapter: HostAdapter
}

/**
 * Connection gate. The backend is a LiteLLM proxy and the user's Virtual Key is
 * the only credential, so the add-in starts even with nothing configured and
 * shows the setup screen until a key and a model are picked.
 */
export function Root({ host, adapter }: RootProps) {
  const { settings, save, clear } = useLiteLlmSettings()
  const defaultBaseUrl = useMemo(() => defaultLiteLlmBaseUrl(), [])
  // Prompts come from the server that delivered the add-in, when there is one.
  const promptServer = usePromptServer()

  // Chat and dictation are real: two models on the same proxy and Virtual Key.
  // Prompts come from the delivery server's mini database when it answers.
  // Skills and documents have no backend yet and stay on the in-memory mock.
  const mock = useMemo(() => createMockBackendClient(), [])
  const backend: BackendClient = useMemo(() => {
    if (!settings) return mock
    const client: BackendClient = {
      ...mock,
      ai: createLiteLlmAI(settings),
      speech: createLiteLlmSpeech({
        baseUrl: settings.baseUrl,
        virtualKey: settings.virtualKey,
        // Empty → dictation reports "kein Modell ausgewählt" instead of faking it.
        model: settings.transcribeModel ?? '',
      }),
    }
    if (!promptServer.available) return client
    // The prompt server authenticates with the same Virtual Key, so each user
    // gets their own prompts next to the shared ones.
    const http = createBackendClient({
      baseUrl: promptServer.baseUrl,
      getAccessToken: async () => settings.virtualKey,
    })
    return { ...client, prompts: http.prompts, systemPrompts: http.systemPrompts }
  }, [mock, settings, promptServer.available, promptServer.baseUrl])

  const loadModels = useCallback(async (baseUrl: string, virtualKey: string) => {
    try {
      const models = await listLiteLlmModels({ baseUrl, virtualKey })
      return models.map((m) => m.id)
    } catch (e) {
      throw new Error(describeConnectionError(e))
    }
  }, [])

  const settingsProps: SettingsViewProps = {
    // The draft always carries a transcription model — '' means "no dictation".
    connection: settings ? { ...settings, transcribeModel: settings.transcribeModel ?? '' } : null,
    defaultBaseUrl,
    onLoadModels: loadModels,
    onSaveConnection: (draft: LiteLlmDraft) => save({ ...settings, ...draft }),
    onDisconnect: clear,
  }

  if (!settings) {
    return (
      <AppShell>
        <Header />
        <SetupView
          defaultBaseUrl={defaultBaseUrl}
          onLoadModels={loadModels}
          onSave={(draft) => save(draft)}
        />
      </AppShell>
    )
  }

  return (
    <SaipProvider backend={backend} adapter={adapter} host={host}>
      <App settings={settingsProps} />
    </SaipProvider>
  )
}

/** Turn a failed /v1/models call into something the user can act on. */
function describeConnectionError(e: unknown): string {
  const code = e instanceof BackendError ? e.code : undefined
  switch (code) {
    case 'unauthorized':
      return 'Virtual Key ungültig oder abgelaufen (401).'
    case 'forbidden':
      return 'Dieser Virtual Key ist für den Server nicht freigegeben (403).'
    case 'not_found':
      return 'Unter dieser Adresse antwortet kein LiteLLM-Server (404). Stimmt die URL?'
    case 'network':
      return 'Server nicht erreichbar. Prüfe die Adresse und deine Netzwerkverbindung.'
    case 'timeout':
      return 'Zeitüberschreitung beim Verbinden. Bitte erneut versuchen.'
    case 'rate_limited':
      return 'Zu viele Anfragen (429). Bitte einen Moment warten.'
    default:
      return e instanceof Error && e.message ? e.message : 'Verbindung fehlgeschlagen.'
  }
}
